import { DIRECTIONS_2, DIRECTIONS_4, timing, variantForLevel, } from './params';
/** Hash LCG déterministe à partir d'un trialIdx + sel. */
function lcg(seed) {
    // Numerical Recipes constants. Keep in [0,1).
    const s = (Math.imul(seed | 0, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
}
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const dirs = variant.nDirections === 2 ? DIRECTIONS_2 : DIRECTIONS_4;
    const r1 = lcg(trialIdx * 7 + 13);
    const r2 = lcg(trialIdx * 31 + 17);
    const r3 = lcg(trialIdx * 53 + 23);
    const centralDirection = dirs[Math.floor(r1 * dirs.length)];
    const incongruent = r2 < variant.incongruentRatio;
    let flankerDirection;
    if (!incongruent) {
        flankerDirection = centralDirection;
    }
    else {
        // Pick a non-central direction from the active set.
        const candidates = dirs.filter((d) => d !== centralDirection);
        flankerDirection = candidates[Math.floor(r3 * candidates.length)];
    }
    let axis;
    if (variant.axis === 'mixed') {
        axis = r3 < 0.5 ? 'horizontal' : 'vertical';
    }
    else {
        axis = variant.axis;
    }
    return {
        trialIdx,
        durationMs: Math.max(50, durationMs),
        centralDirection,
        flankerDirection,
        congruent: !incongruent,
        axis,
    };
}
export function createLostInMigrationAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(answer, stim) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const correct = answer === stim.centralDirection;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ answer, reactionTimeMs: rt, correct });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        // Timeout fenêtre réponse.
                        timers.push(setTimeout(() => {
                            // Pas de réponse : on résout avec une réponse "fausse" par défaut.
                            if (!resolver)
                                return;
                            const r = resolver;
                            resolver = null;
                            rejecter = null;
                            // Réponse arbitraire : on prend l'opposé du central pour rester
                            // déterministe et marqué incorrect.
                            const fallback = stim.centralDirection === 'left'
                                ? 'right'
                                : stim.centralDirection === 'right'
                                    ? 'left'
                                    : stim.centralDirection === 'up'
                                        ? 'down'
                                        : 'up';
                            r({
                                answer: fallback,
                                reactionTimeMs: timing.responseWindowMs,
                                correct: false,
                            });
                        }, timing.responseWindowMs));
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(direction) {
            if (state.subPhase !== 'awaiting_input' && state.subPhase !== 'stimulus') {
                return;
            }
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(direction, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map